import { ModuleFallback } from '@e2grnd/ui-core/dist/components/loaders/ModuleFallback'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'
import CalculatorLayout from './components/CalculatorLayout'
import layoutMap from './meta/page-layouts.json'
import { DynamicCalculatorPage } from './routes/calculator-routes'
import { routes } from './routes/route-config'

// Report Core Web Vitals — logs in dev, sends to PostHog/Sentry in prod
function reportWebVital({ name, value }: { name: string; value: number }) {
  if (import.meta.env.DEV) {
    // biome-ignore lint/suspicious/noConsole: intentional dev-only logging
    console.log(`[Web Vital] ${name}: ${Math.round(value)}${name === 'CLS' ? '' : 'ms'}`)
  }
}
onCLS(reportWebVital)
onINP(reportWebVital)
onLCP(reportWebVital)
onFCP(reportWebVital)
onTTFB(reportWebVital)

// Enable Why Did You Render in development
if (import.meta.env.DEV) {
  // Uncomment to enable
  // import('@welldone-software/why-did-you-render').then((whyDidYouRender) => {
  //   whyDidYouRender.default(React, {
  //     trackAllPureComponents: true,
  //   })
  // })
}

// Clean up legacy Gatsby service worker and its caches.
// The kill-switch sw.js (in static/) handles users whose old SW is still active.
// This code handles the belt-and-suspenders case: SW already gone but caches linger.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (const registration of registrations) {
      registration.unregister()
    }
  })
}
if ('caches' in window) {
  caches.keys().then(names => {
    for (const name of names) {
      caches.delete(name)
    }
  })
}

// Handle stale chunks after deployment. When new builds replace old hashed
// chunks, users with the old page open get import errors on navigation.
// Vite fires this event when its preload wrapper fails. We reload once to
// pick up the fresh index.html; sessionStorage prevents infinite loops.
window.addEventListener('vite:preloadError', () => {
  const key = 'vite-chunk-reload'
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1')
    window.location.reload()
  }
})

// Fallback UI when a chunk error escapes the auto-reload (e.g., server down).
// This renders outside the MUI ThemeProvider so it uses inline styles matching
// the app's ErrorPage visual language (centered card, blue gradient header).
function ChunkErrorFallback() {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        backgroundColor: '#fafafa',
      }}
    >
      <div
        style={{
          maxWidth: 345,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            height: 120,
            background: 'linear-gradient(230deg, #00B2E3 0%, #002D74 95%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '1.25rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}
        >
          Equity Engineering Cloud
        </div>
        <div style={{ padding: '16px' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 400, color: 'rgba(0,0,0,0.87)' }}>
            A new version is available
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.43 }}>
            The application has been updated. Please reload the page to continue.
          </p>
        </div>
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem('vite-chunk-reload')
              window.location.reload()
            }}
            style={{
              padding: '6px 16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.02857em',
              cursor: 'pointer',
              borderRadius: 4,
              border: 'none',
              background: '#1976d2',
              color: '#fff',
              lineHeight: 1.75,
            }}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  )
}

// Layout wrapper that determines which layout to use based on route
function LayoutWrapper({ layoutType, children }: { layoutType?: string; children?: React.ReactNode }) {
  const location = useLocation()

  // Create pageContext object for layout configuration
  // The layout props (noDrawer, noTopNav) must be nested under frontmatter.layout
  // to match what CalculatorLayout expects
  const pageContext = {
    frontmatter: {
      bare: layoutType === 'bare',
      layout: {
        noDrawer: layoutType === 'no-drawer' || layoutType === 'no-drawer-public' || layoutType === 'landing',
        noTopNav: layoutType === 'landing',
        isPublic: layoutType === 'public' || layoutType === 'no-drawer-public',
      },
    },
  }

  return (
    <CalculatorLayout pageContext={pageContext} location={location}>
      {children || <Outlet />}
    </CalculatorLayout>
  )
}

// Bare layout - minimal providers without TopNav/Drawer/Footer
function BareLayout() {
  const location = useLocation()
  return (
    <CalculatorLayout pageContext={{ frontmatter: { bare: true } }} location={location}>
      <Outlet />
    </CalculatorLayout>
  )
}

// Render route component — eager routes render directly, lazy routes get a Suspense boundary
function RouteElement({
  component: Component,
  eager,
}: {
  component: React.ComponentType<Record<string, never>>
  eager?: boolean
}) {
  if (eager) {
    return <Component />
  }
  return (
    <Suspense fallback={<ModuleFallback />}>
      <Component />
    </Suspense>
  )
}

// Separate routes by layout type
const bareRoutes = routes.filter(route => layoutMap[route.path as keyof typeof layoutMap] === 'bare')
const mainRoutes = routes.filter(route => {
  const layoutType = layoutMap[route.path as keyof typeof layoutMap]
  return layoutType !== 'bare' && !layoutType
})
const customLayoutRoutes = routes.filter(route => {
  const layoutType = layoutMap[route.path as keyof typeof layoutMap]
  return layoutType && layoutType !== 'bare'
})

// Use createBrowserRouter for data router features (useBlocker, etc.)
const router = createBrowserRouter([
  {
    errorElement: <ChunkErrorFallback />,
    children: [
      // Routes with bare layout
      {
        element: <BareLayout />,
        children: bareRoutes.map(route => ({
          path: route.path,
          element: <RouteElement component={route.component} eager={route.eager} />,
        })),
      },

      // Routes with custom layouts (landing, no-drawer, public)
      ...customLayoutRoutes.map(route => {
        const routeLayoutType = layoutMap[route.path as keyof typeof layoutMap]
        return {
          path: route.path,
          element: (
            <LayoutWrapper layoutType={routeLayoutType}>
              <RouteElement component={route.component} eager={route.eager} />
            </LayoutWrapper>
          ),
        }
      }),

      // Routes with main layout (default)
      {
        element: <LayoutWrapper layoutType="main" />,
        children: [
          ...mainRoutes.map(route => ({
            path: route.path,
            element: <RouteElement component={route.component} eager={route.eager} />,
          })),

          // Dynamic calculator route
          {
            path: '/app/calculator/:calcId/*',
            element: <DynamicCalculatorPage />,
          },

          // Embed route — dedicated path for iframe embedding.
          // Renders the same calculator page but the /app/embed/ prefix
          // triggers chromeless mode automatically (no query param needed).
          {
            path: '/app/embed/calculator/:calcId/*',
            element: <DynamicCalculatorPage />,
          },

          // Legacy redirects
          { path: '/app/admin', element: <Navigate to="/app/admin/search-jobs" replace /> },
          { path: '/product', element: <Navigate to="/app" replace /> },
        ],
      },

      // Default fallback
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
])

const root = document.getElementById('root')

if (!root) {
  throw new Error('Root element not found')
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
)
